import React, { useState, useEffect, useRef } from 'react';
import * as PropTypes from 'prop-types';
import { fetchBlogTags } from '../../Util/ApiService';

/**
 * Mirrors BlogTag::INVALID_NAME_PATTERN
 */
export const INVALID_TAG_NAME_PATTERN =
  /[\u{1F000}-\u{1FAFF}]|\u{FE0F}|\u{20E3}|\p{C}/u;
export const TAG_NAME_MAX_LENGTH = 255;

const SUGGESTION_DEBOUNCE_MS = 250;

export const normaliseTagName = (value) => {
  return String(value === null || value === undefined ? '' : value)
    .replace(/\s+/gu, ' ')
    .trim();
};

export const validateTagName = (name) => {
  if (INVALID_TAG_NAME_PATTERN.test(name)) {
    return 'Tag names cannot contain emoji.';
  }
  if (name.length > TAG_NAME_MAX_LENGTH) {
    return `Tag names cannot be longer than ${TAG_NAME_MAX_LENGTH} characters.`;
  }

  return '';
};

const renderHighlightedName = (name, term) => {
  const matchIndex = name.toLowerCase().indexOf(term.toLowerCase());
  if (matchIndex < 0 || term === '') {
    return name;
  }

  return (
    <>
      {name.slice(0, matchIndex)}
      <span className="font-bold">
        {name.slice(matchIndex, matchIndex + term.length)}
      </span>
      {name.slice(matchIndex + term.length)}
    </>
  );
};

const BlogTagsField = ({
  tags = [],
  onChange = () => {},
  searchUri = '',
  mostUsedTags = []
}) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [error, setError] = useState('');
  const latestRequestId = useRef(0);

  const searchTerm = normaliseTagName(input);

  const isAssigned = (name) => {
    return tags.some((tag) => {
      return tag.toLowerCase() === name.toLowerCase();
    });
  };

  const visibleSuggestions = suggestions.filter((suggestion) => {
    return !isAssigned(suggestion.name);
  });

  const clearSuggestions = () => {
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const addTag = (rawName) => {
    const name = normaliseTagName(rawName);
    if (name === '') {
      setInput('');
      return;
    }

    const validationError = validateTagName(name);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!isAssigned(name)) {
      onChange([...tags, name]);
    }

    setInput('');
    setError('');
    clearSuggestions();
  };

  const removeTag = (index) => {
    onChange(
      tags.filter((_, tagIndex) => {
        return tagIndex !== index;
      })
    );
  };

  useEffect(() => {
    if (searchTerm === '' || searchUri === '') {
      clearSuggestions();
      return undefined;
    }

    latestRequestId.current += 1;
    const requestId = latestRequestId.current;

    const timer = window.setTimeout(async () => {
      const response = await fetchBlogTags(searchUri, { q: searchTerm });
      if (latestRequestId.current !== requestId) {
        return;
      }
      setSuggestions(response && response.tags ? response.tags : []);
      setHighlightedIndex(-1);
    }, SUGGESTION_DEBOUNCE_MS);

    return () => {
      return window.clearTimeout(timer);
    };
  }, [searchTerm, searchUri]);

  const moveHighlight = (step) => {
    if (visibleSuggestions.length === 0) {
      return;
    }
    setHighlightedIndex((current) => {
      const next = current + step;
      if (next < 0) {
        return visibleSuggestions.length - 1;
      }
      return next >= visibleSuggestions.length ? 0 : next;
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(e.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const highlighted =
        e.key === 'Enter' ? visibleSuggestions[highlightedIndex] : null;
      addTag(highlighted ? highlighted.name : input);
      return;
    }

    if (e.key === 'Escape') {
      clearSuggestions();
      return;
    }

    if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        {/* the suggestions overlay whatever follows instead of pushing it down */}
        <div className="relative">
          <label
            htmlFor="blog-tags-input"
            className="block font-semibold text-gray-800 text-lg"
          >
            Add tag
            <input
              id="blog-tags-input"
              type="text"
              autoComplete="off"
              className="block text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setError('');
              }}
              onKeyDown={onKeyDown}
              onBlur={() => {
                if (input !== '') {
                  addTag(input);
                }
              }}
            />
          </label>

          {visibleSuggestions.length > 0 && (
            <ul className="absolute left-0 right-0 top-full z-10 bg-white shadow-lg border border-blue-500 border-t-0 rounded-b-sm max-h-60 overflow-y-auto">
              {visibleSuggestions.map((suggestion, index) => {
                return (
                  <li key={suggestion.blogTagId}>
                    <button
                      type="button"
                      aria-label={suggestion.name}
                      className={`w-full text-left px-3 py-2 ${
                        index === highlightedIndex
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-gray-100'
                      }`}
                      onMouseDown={(e) => {
                        return e.preventDefault();
                      }}
                      onMouseEnter={() => {
                        return setHighlightedIndex(index);
                      }}
                      onClick={() => {
                        return addTag(suggestion.name);
                      }}
                    >
                      {renderHighlightedName(suggestion.name, searchTerm)}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {error !== '' ? (
          <p className="text-sm text-red-500 mt-1">{error}</p>
        ) : (
          <p className="text-sm text-gray-500 mt-1">
            Separate with commas or the Enter key.
          </p>
        )}
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {tags.map((tag, index) => {
            return (
              <span
                key={tag}
                className="inline-flex px-3 py-1 border border-slate-200 items-center gap-x-2 font-semibold rounded-sm"
              >
                <span className="text-sm">{tag}</span>
                <button
                  type="button"
                  aria-label={`Remove ${tag}`}
                  className="text-xs"
                  onClick={() => {
                    return removeTag(index);
                  }}
                >
                  x
                </button>
              </span>
            );
          })}
        </div>
      )}

      {mostUsedTags.length > 0 && (
        <div>
          <span className="font-semibold text-gray-800 text-base">
            Most used
          </span>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
            {mostUsedTags.map((name) => {
              return (
                <button
                  key={name}
                  type="button"
                  className="text-blue-600 underline hover:no-underline"
                  onClick={() => {
                    return addTag(name);
                  }}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogTagsField;

BlogTagsField.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  searchUri: PropTypes.string,
  mostUsedTags: PropTypes.arrayOf(PropTypes.string)
};
